import { useState, useCallback } from 'react';

interface RuleEvaluationResult {
  allowed: boolean;
  reason?: string;
  ruleOutputs?: Record<string, unknown> | null;
}

interface UseRuleEvaluationOptions {
  ruleId: string;
  context: Record<string, unknown>;
  tenantId?: string;
}

interface UseRuleEvaluationReturn {
  evaluate: () => Promise<RuleEvaluationResult>;
  isEvaluating: boolean;
  result: RuleEvaluationResult | null;
  error: string | null;
}

export function useRuleEvaluation({
  ruleId,
  context,
  tenantId,
}: UseRuleEvaluationOptions): UseRuleEvaluationReturn {
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [result, setResult] = useState<RuleEvaluationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const evaluate = useCallback(async (): Promise<RuleEvaluationResult> => {
    setIsEvaluating(true);
    setError(null);

    try {
      const response = await fetch('/api/v1/workflow-rules/evaluate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ruleId, context, tenantId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Rule evaluation failed');
      }

      const data: RuleEvaluationResult = await response.json();
      setResult(data);
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Rule evaluation failed';
      setError(message);
      const failResult: RuleEvaluationResult = {
        allowed: true,
        reason: undefined,
        ruleOutputs: null,
      };
      setResult(failResult);
      return failResult;
    } finally {
      setIsEvaluating(false);
    }
  }, [ruleId, context, tenantId]);

  return { evaluate, isEvaluating, result, error };
}

export type { RuleEvaluationResult, UseRuleEvaluationReturn };
